import {
  Box, Card, CardContent, Chip, Divider, Grid, LinearProgress, Stack, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'

const fmtKg = (v, dp = 3) => (v == null || isNaN(v) ? '—' : `${Number(v).toFixed(dp)} kg`)
const fmtG = (v) => (v == null || isNaN(v) ? '—' : `${Number(v).toFixed(0)} g`)

// ── Weight Flow comparison bars ─────────────────────────────────────────────
function CompareBar({ label, value, max, color }) {
  const pct = max && value ? Math.max(2, Math.round((value / max) * 100)) : 0
  return (
    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 0.75 }}>
      <Typography sx={{ fontSize: 11.5, color: '#374151', minWidth: 60 }}>{label}</Typography>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <LinearProgress
          variant='determinate'
          value={pct}
          sx={{
            height: 8,
            borderRadius: 1,
            bgcolor: '#f3f4f6',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 1 },
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 11.5, color: '#111', fontWeight: 600, minWidth: 56, textAlign: 'right' }}>
        {value == null ? '—' : Number(value).toFixed(3)}
      </Typography>
    </Stack>
  )
}

// ── Single weight-stage card ────────────────────────────────────────────────
function FlowCard({ title, sub, value, accent, bg, clickable, onClick }) {
  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        position: 'relative',
        borderRadius: 1,
        bgcolor: bg,
        py: 1, px: 1.25,
        borderLeft: `3px solid ${accent}`,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background-color 0.15s',
        height: '100%', // fill the grid cell so paired cards line up
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': clickable ? { bgcolor: bg, filter: 'brightness(0.97)' } : undefined,
      }}
    >
      <Box sx={{ minWidth: 0, mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111', lineHeight: 1.25 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: '#6b7280', mt: 0.25, lineHeight: 1.3 }}>
          {sub}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: clickable ? accent : '#111',
          textDecoration: clickable ? 'underline' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          whiteSpace: 'nowrap',
          alignSelf: 'flex-end',
        }}
      >
        {fmtKg(value)}
        {clickable && <OpenInNewIcon sx={{ fontSize: 12 }} />}
      </Typography>
    </Box>
  )
}

// ── Discrepancy comparison block ────────────────────────────────────────────
// Shown at the foot of the Weight Flow card. Computed client-side from the
// two named fields so the label reflects the exact comparison (the API's
// top-level discrepancyKg compares different fields and is used elsewhere).
function DiscrepancyBlock({ label, declared, actual }) {
  const hasBoth = declared != null && actual != null && Number(declared) > 0
  if (!hasBoth) {
    return (
      <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: '#f9fafb' }}>
        <Typography sx={{ fontSize: 11.5, color: '#6b7280' }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 0.5, fontStyle: 'italic' }}>
          Awaiting MIS data —
        </Typography>
      </Box>
    )
  }
  const diffKg = Number(actual) - Number(declared)
  const diffPct = (diffKg / Number(declared)) * 100
  const absPct = Math.abs(diffPct)
  // Severity stepping mirrors the BE discrepancyLevel buckets in the doc
  const severity =
    absPct === 0 ? { color: '#16a34a', bg: '#dcfce7' }
      : absPct < 15 ? { color: '#a16207', bg: '#fef9c3' }
        : absPct < 30 ? { color: '#c2410c', bg: '#ffedd5' }
          : { color: '#b91c1c', bg: '#fee2e2' }
  const fillPct = Math.min(100, absPct)
  const sign = diffKg > 0 ? '+' : diffKg < 0 ? '−' : ''
  return (
    <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: '#f9fafb' }}>
      <Typography sx={{ fontSize: 11.5, color: '#6b7280' }}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: severity.color, mt: 0.25, mb: 0.75 }}>
        {sign}{Math.abs(diffKg).toFixed(3)} kg ({diffPct.toFixed(0)}%)
      </Typography>
      <LinearProgress
        variant='determinate'
        value={fillPct}
        sx={{
          height: 8,
          borderRadius: 1,
          bgcolor: severity.bg,
          '& .MuiLinearProgress-bar': { bgcolor: severity.color, borderRadius: 1 },
        }}
      />
    </Box>
  )
}

// ── AWB-level snapshot tile (compact) ───────────────────────────────────────
function SnapshotTile({ title, snap, accent }) {
  const dims =
    snap?.lengthCm && snap?.breadthCm && snap?.heightCm
      ? `${snap.lengthCm} × ${snap.breadthCm} × ${snap.heightCm} cm`
      : '—'
  return (
    <Card sx={{ borderTop: `3px solid ${accent}` }}>
      <CardContent sx={{ py: 1.25, px: 1.75 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.75 }}>{title}</Typography>
        <Grid container rowSpacing={0.5} columnSpacing={2}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: 10.5, color: '#6b7280' }}>Dead Wt</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{fmtG(snap?.deadWeightGms)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: 10.5, color: '#6b7280' }}>Vol Wt</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{fmtG(snap?.volWeightGms)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: 10.5, color: '#6b7280' }}>Applicable</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: accent }}>
              {fmtG(snap?.applicableWeightGms)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: 10.5, color: '#6b7280' }}>L × B × H</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{dims}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default function RowExpansion({ row, onPredictedClick }) {
  const products = row?.products || []
  const totalUnits = row?.totalUnits ?? products.reduce((s, p) => s + (p.quantity || 0), 0)
  const w = row?.weights || {}

  // Bar comparison: scale to the biggest of the 4 primary values
  const compareMax = Math.max(
    w.clientDeclared || 0,
    w.prozoAllocated || 0,
    w.courierBilledWeightKg || 0,
    w.weightChargedToClientKg || 0,
    0.001,
  )

  // Stage cards definition — colours mirror the WeightFlowHeader stages
  const stages = [
    { title: 'API Weight Pushed by Client', sub: 'Weight declared by client via API / OMS.', value: w.clientDeclared, accent: '#3b82f6', bg: '#eff6ff' },
    { title: 'Predicted Weight', sub: 'Weight Proship Predicted.', value: w.proshipPushed, accent: '#6366f1', bg: '#eef2ff' },
    { title: 'Prozo Allocated Weight', sub: 'Allocated via past database with packaging adjustment.', value: w.prozoAllocated, accent: '#8b5cf6', bg: '#f5f3ff', clickable: true },
    { title: 'Transit Charged Weight', sub: 'Transit weight charged by courier.', value: w.transitWeightKg, accent: '#f97316', bg: '#fff7ed' },
    { title: 'Courier Billed Weight', sub: 'Actual weight billed by courier.', value: w.courierBilledWeightKg, accent: '#f59e0b', bg: '#fffbeb' },
    { title: 'Courier Billed Slab', sub: 'Slab-rounded weight per courier rate card.', value: w.courierBilledSlabKg, accent: '#ef4444', bg: '#fef2f2' },
    { title: 'Weight Charged to Client', sub: 'Weight on the merchant invoice.', value: w.weightChargedToClientKg, accent: '#10b981', bg: '#ecfdf5' },
    { title: 'Weight Slab Billed to Client', sub: 'Slab-rounded weight on the merchant invoice.', value: w.weightSlabBilledToClientKg, accent: '#10b981', bg: '#ecfdf5' },
  ]

  return (
    <Box sx={{ p: 2, bgcolor: '#fafbfc' }}>
      <Grid container spacing={2} alignItems='stretch'>
        {/* LEFT — SKU breakdown stacked with the two AWB-level snapshots
            so the column fills with content rather than whitespace. */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>SKU-Level Breakdown</Typography>
                  <Chip
                    size='small'
                    label={`${products.length} SKU${products.length === 1 ? '' : 's'} · ${totalUnits} unit${totalUnits === 1 ? '' : 's'}`}
                    sx={{ height: 22, fontSize: 11 }}
                  />
                </Stack>
                <Table size='small' sx={{ '& td, & th': { fontSize: 12, py: 0.75 } }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { color: '#6b7280', fontWeight: 700, bgcolor: '#f5f7fa' } }}>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align='right'>Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                          No SKU data available for this shipment.
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((p, i) => (
                        <TableRow key={`${p.sku || 'na'}-${i}`}>
                          <TableCell>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>
                              {p.sku || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{p.skuName || '—'}</TableCell>
                          <TableCell align='right'>{p.quantity ?? '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <Divider sx={{ my: 1 }} />
                <Stack direction='row' justifyContent='space-between'>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Shipment Total</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{totalUnits} units</Typography>
                </Stack>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SnapshotTile title='Client Declared Snapshot' snap={row?.clientDeclaredSnapshot} accent='#3b82f6' />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SnapshotTile title='Predicted Weight Snapshot' snap={row?.proshipPushedSnapshot} accent='#6366f1' />
              </Grid>
            </Grid>
          </Stack>
        </Grid>

        {/* RIGHT — Weight Flow with stage cards in a 2-col grid so the
            right column doesn't tower over the left. */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                <ScaleOutlinedIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Weight Flow</Typography>
              </Stack>

              {/* Comparison bars — proportional to the largest of the 4 */}
              <Box sx={{ mb: 1.5 }}>
                <CompareBar label='Client' value={w.clientDeclared} max={compareMax} color='#93c5fd' />
                <CompareBar label='Prozo' value={w.prozoAllocated} max={compareMax} color='#a78bfa' />
                <CompareBar label='Courier' value={w.courierBilledWeightKg} max={compareMax} color='#fdba74' />
                <CompareBar label='Charged' value={w.weightChargedToClientKg} max={compareMax} color='#f87171' />
              </Box>

              {/* Stage cards in a 2-col grid */}
              <Grid container spacing={1}>
                {stages.map((s) => (
                  <Grid item xs={12} sm={6} key={s.title}>
                    <FlowCard
                      title={s.title}
                      sub={s.sub}
                      value={s.value}
                      accent={s.accent}
                      bg={s.bg}
                      clickable={!!s.clickable}
                      onClick={() => s.clickable && onPredictedClick?.(row)}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Discrepancy: courier transit weight vs what the client declared */}
              <DiscrepancyBlock
                label='Client vs Transit Charged Discrepancy'
                declared={w.clientDeclared}
                actual={w.transitWeightKg}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
